import merge from 'lodash/merge';
import { combineReducers } from 'redux';
import { normalize } from 'normalizr';

import { boardSchema } from '../../schemas';
import {
  GET_BOARDS_FULFILLED,
  GET_BOARDS_PENDING,
  GET_BOARDS_REJECTED,
  GET_BOARD_ARTICLES_FULFILLED,
  GET_BOARD_ARTICLES_PENDING,
  GET_BOARD_ARTICLES_REJECTED,
  GET_BOARD_HOT_ARTICLES_FULFILLED,
  GET_BOARD_HOT_ARTICLES_PENDING,
  GET_BOARD_HOT_ARTICLES_REJECTED,
  GET_BOARD_INFO_FULFILLED,
  GET_BOARD_INFO_PENDING,
  GET_BOARD_INFO_REJECTED,
  GET_BOARD_TOP_USERS_FULFILLED,
  GET_BOARD_TOP_USERS_PENDING,
  GET_BOARD_TOP_USERS_REJECTED,
  GET_BOARD_TRENDS_FULFILLED,
  GET_BOARD_TRENDS_PENDING,
  GET_BOARD_TRENDS_REJECTED,
  GET_BOARD_USER_COUNT_FULFILLED,
  GET_BOARD_USER_COUNT_PENDING,
  GET_BOARD_USER_COUNT_REJECTED,
} from '../../types';

// State interfaces
interface AllState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface InfoState {
  board: string | null;
  entities: any;
  isFetching: boolean;
  error: any | null;
}

interface TrendsState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface ArticlesState {
  board: string | null;
  data: any[];
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface HotArticlesState {
  board: string | null;
  data: any[];
  sinceDaysAgo: number;
  offset: number;
  limit: number;
  isFetching: boolean;
  error: any | null;
}

interface TopUsersState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface UserCountState {
  data: any[];
  isFetching: boolean;
  error: any | null;
}

interface BoardState {
  all: AllState;
  articles: ArticlesState;
  hotArticles: HotArticlesState;
  info: InfoState;
  topUsers: TopUsersState;
  trends: TrendsState;
  userCount: UserCountState;
}

// Action interfaces
interface PendingAction {
  type: typeof GET_BOARDS_PENDING | typeof GET_BOARD_INFO_PENDING |
        typeof GET_BOARD_TRENDS_PENDING | typeof GET_BOARD_ARTICLES_PENDING |
        typeof GET_BOARD_HOT_ARTICLES_PENDING | typeof GET_BOARD_TOP_USERS_PENDING |
        typeof GET_BOARD_USER_COUNT_PENDING;
}

interface RejectedAction {
  type: typeof GET_BOARDS_REJECTED | typeof GET_BOARD_INFO_REJECTED |
        typeof GET_BOARD_TRENDS_REJECTED | typeof GET_BOARD_ARTICLES_REJECTED |
        typeof GET_BOARD_HOT_ARTICLES_REJECTED | typeof GET_BOARD_TOP_USERS_REJECTED |
        typeof GET_BOARD_USER_COUNT_REJECTED;
  error: any;
}

interface GetBoardsFulfilledAction {
  type: typeof GET_BOARDS_FULFILLED;
  payload: {
    data: any[];
  };
}

interface GetBoardInfoFulfilledAction {
  type: typeof GET_BOARD_INFO_FULFILLED;
  payload: {
    data: any;
  };
}

interface GetBoardDataFulfilledAction {
  type: typeof GET_BOARD_TRENDS_FULFILLED | typeof GET_BOARD_TOP_USERS_FULFILLED |
        typeof GET_BOARD_USER_COUNT_FULFILLED;
  payload: {
    data: any[];
  };
}

interface GetBoardArticlesFulfilledAction {
  type: typeof GET_BOARD_ARTICLES_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    board: string;
    offset: number;
    limit: number;
  };
}

interface GetBoardHotArticlesFulfilledAction {
  type: typeof GET_BOARD_HOT_ARTICLES_FULFILLED;
  payload: {
    data: any[];
  };
  meta: {
    board: string;
    sinceDaysAgo: number;
    offset: number;
    limit: number;
  };
}

type AllAction = PendingAction | GetBoardsFulfilledAction | RejectedAction;
type InfoAction = PendingAction | GetBoardInfoFulfilledAction | RejectedAction;
type DataAction = PendingAction | GetBoardDataFulfilledAction | RejectedAction;
type ArticlesAction = PendingAction | GetBoardArticlesFulfilledAction | RejectedAction;
type HotArticlesAction = PendingAction | GetBoardHotArticlesFulfilledAction | RejectedAction;

const INITIAL_STATE = {
  ALL: {
    data: [],
    isFetching: false,
    error: null,
  },
  INFO: {
    board: null,
    entities: {},
    isFetching: false,
    error: null,
  },
  TRENDS: {
    data: [],
    isFetching: false,
    error: null,
  },
  ARTICLES: {
    board: null,
    data: [],
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  HOT_ARTICLES: {
    board: null,
    data: [],
    sinceDaysAgo: 1,
    offset: 0,
    limit: 0,
    isFetching: false,
    error: null,
  },
  TOP_USERS: {
    data: [],
    isFetching: false,
    error: null,
  },
  USER_COUNT: {
    data: [],
    isFetching: false,
    error: null,
  },
};

const all = (state: AllState = INITIAL_STATE.ALL, action: AllAction): AllState => {
  switch (action.type) {
    case GET_BOARDS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_BOARDS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_BOARDS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const info = (state: InfoState = INITIAL_STATE.INFO, action: InfoAction): InfoState => {
  switch (action.type) {
    case GET_BOARD_INFO_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_BOARD_INFO_FULFILLED: {
      const {
        data,
      } = action.payload;
      if (!data) {
        return INITIAL_STATE.INFO;
      }
      const {
        entities,
        result,
      } = normalize(data, boardSchema);

      return {
        entities: merge(state.entities, entities),
        board: result,
        isFetching: false,
        error: null,
      };
    }
    case GET_BOARD_INFO_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const trends = (state: TrendsState = INITIAL_STATE.TRENDS, action: DataAction): TrendsState => {
  switch (action.type) {
    case GET_BOARD_TRENDS_PENDING:
      return {
        ...INITIAL_STATE.TRENDS,
        isFetching: true,
      };
    case GET_BOARD_TRENDS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_BOARD_TRENDS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const articles = (state: ArticlesState = INITIAL_STATE.ARTICLES, action: ArticlesAction): ArticlesState => {
  switch (action.type) {
    case GET_BOARD_ARTICLES_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_BOARD_ARTICLES_FULFILLED: {
      const {
        board,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        board,
        data,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_BOARD_ARTICLES_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const hotArticles = (state: HotArticlesState = INITIAL_STATE.HOT_ARTICLES, action: HotArticlesAction): HotArticlesState => {
  switch (action.type) {
    case GET_BOARD_HOT_ARTICLES_PENDING:
      return {
        ...INITIAL_STATE.HOT_ARTICLES,
        isFetching: true,
      };
    case GET_BOARD_HOT_ARTICLES_FULFILLED: {
      const {
        board,
        sinceDaysAgo,
        offset,
        limit,
      } = action.meta;
      const {
        data,
      } = action.payload;
      return {
        board,
        data,
        sinceDaysAgo,
        offset,
        limit,
        isFetching: false,
        error: null,
      };
    }
    case GET_BOARD_HOT_ARTICLES_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const topUsers = (state: TopUsersState = INITIAL_STATE.TOP_USERS, action: DataAction): TopUsersState => {
  switch (action.type) {
    case GET_BOARD_TOP_USERS_PENDING:
      return {
        ...state,
        isFetching: true,
      };
    case GET_BOARD_TOP_USERS_FULFILLED: {
      const {
        data,
      } = action.payload;

      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_BOARD_TOP_USERS_REJECTED:
      return {
        ...state,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

const userCount = (state: UserCountState = INITIAL_STATE.USER_COUNT, action: DataAction): UserCountState => {
  switch (action.type) {
    case GET_BOARD_USER_COUNT_PENDING:
      return {
        ...INITIAL_STATE.USER_COUNT,
        isFetching: true,
      };
    case GET_BOARD_USER_COUNT_FULFILLED: {
      const {
        data,
      } = action.payload;
      return {
        data,
        isFetching: false,
        error: null,
      };
    }
    case GET_BOARD_USER_COUNT_REJECTED:
      return {
        ...INITIAL_STATE.USER_COUNT,
        error: action.error,
        isFetching: false,
      };
    default:
      return state;
  }
};

export default combineReducers<BoardState>({
  all,
  articles,
  hotArticles,
  info,
  topUsers,
  trends,
  userCount,
});